/**
 * The person ids a Base Toolset tool's answer names to the agent that called it (wayai-pro/wayai#5473 §3
 * "Knowing one's own id", owner decision Q-H = T3): so an agent serving staff can write a person field from
 * them — a doctor's agent writing `doctor_id`.
 *
 * **Only on a staff call.** An answer carries them only when the call's audience is `staff` (the persons it
 * sends, `REKOR_BOUND_PERSON_HEADER`) and its agent is not customer-facing: never on a customer-facing call,
 * whose answer the customer may be shown, so neither a customer's id nor a consulting team member's reaches
 * one. A hub page's answer never carries them: a page is no model.
 *
 * **What it names.** Each person-bound party of the call that has a person id, in the call's order, by which
 * party it is (`TOOLSET_ANSWER_PERSON_PARTIES`) and its primary person id — the one its account derives in the
 * hub's org, never a person link's, never a raw account id. A party holding `$person` is named by its own id
 * too. A party with no id (an unredeemed team invite) is not named, and an answer naming no one carries no key.
 *
 * **Where.** One top-level key of the answer the agent reads, `TOOLSET_ANSWER_PERSONS_KEY`, beside the
 * toolset's own answer: on a success, a key of the toolset's answer object (`withToolsetAnswerPersons`); on a
 * refusal, beside its `error`. The data product's answers carry no such key; if one ever did, the answer is
 * wrapped (`result`) rather than the key overwritten or kept.
 */

import { z } from 'zod';
import { REKOR_MAX_PERSON_PARTIES } from './rekor-attestation';
import { rekorPersonIdSchema } from './rekor-toolset';

/** The answer key that names the call's persons. */
export const TOOLSET_ANSWER_PERSONS_KEY = 'wayai_persons';

/**
 * Which party of the call a named person is:
 *   - `requester`: the conversation's own person (on a staff call, a team member in their own conversation);
 *   - `originator`: the person whose message started the turn, when they are not its requester — a team member's
 *     send — or an eval run's starter;
 *   - `initiator`: the team member who opened the consult thread the turn runs in, on a turn no message of theirs
 *     started (a consultant answering, a schedule) — their own message in it makes them its `originator`;
 *   - `delegator`: a person the call that delegated this task was for, on a delegated task's turn.
 */
export const TOOLSET_ANSWER_PERSON_PARTIES = ['requester', 'originator', 'initiator', 'delegator'] as const;
export type ToolsetAnswerPersonParty = (typeof TOOLSET_ANSWER_PERSON_PARTIES)[number];

/** One named person: which party it is, and its primary person id. */
export const toolsetAnswerPersonSchema = z.object({
  party: z.enum(TOOLSET_ANSWER_PERSON_PARTIES),
  person_id: rekorPersonIdSchema,
}).strict();
export type ToolsetAnswerPerson = z.infer<typeof toolsetAnswerPersonSchema>;

/** The persons an answer names: at least one, at most a call's parties. */
export const toolsetAnswerPersonsSchema = z.array(toolsetAnswerPersonSchema).min(1).max(REKOR_MAX_PERSON_PARTIES);

/**
 * A toolset's answer as the agent reads it, naming `persons`: the answer object with the key beside its own
 * keys, or, for an answer that is not a plain object or already holds the key, `{ result: answer }` beside it.
 */
export function withToolsetAnswerPersons(answer: unknown, persons: readonly ToolsetAnswerPerson[]): Record<string, unknown> {
  const named = { [TOOLSET_ANSWER_PERSONS_KEY]: [...persons] };
  // A plain object, checked inline: `isPlainRecord` (`interactive-bindings.ts`) would bring that whole module
  // into every Worker closure this contract reaches.
  if (typeof answer === 'object' && answer !== null && !Array.isArray(answer)
    && !Object.prototype.hasOwnProperty.call(answer, TOOLSET_ANSWER_PERSONS_KEY)) {
    return { ...(answer as Record<string, unknown>), ...named };
  }
  return { result: answer, ...named };
}
